import { isActiveBidKeyFeaturesReview } from "components/bidKeyFeaturesReviewTour/types";
import { getRelayConnectionInitialStep, getRelayConnectionModalParams } from "components/modal/relayConnection";
import { organizationDemoPeriodStatusesEnum, organizationRegistrationStatusesEnum } from "helpers/enums";
import { isImpersonatingUser } from "helpers/localStorage";
import React, { useCallback, useEffect, useRef, useState } from "react";
import SignalRConnection from "services/signalRConnection";
import { productMetricsEventNames, sendEventLog } from "sources/eventLogs";
import { applyOnboarding, getOnboarding } from "sources/profile/user";

const SHOW_LIMIT = 5;
const initialOnboarding = {
    isOnboardingFinished: false,
    onboardingCount: 0,
};

const Monitoring = (props) => {
    const { organization, roles } = props;
    const isOnboardingSuppressed = roles?.isIntegrationAccount || isImpersonatingUser();

    const onboardingOptionsRef = useRef(initialOnboarding);
    const [isLoading, setIsLoading] = useState(true);
    const [isRelayModalOpened, setIsRelayModalOpened] = useState(false);
    const relay = organization?.relay ?? {};
    const showConnectionModal = relay.showConnectionModal;
    const relayConnectionInitialStep = getRelayConnectionInitialStep(relay);

    const isCompletedTruckingOrganizationAdmin = useCallback(
        () =>
            roles?.isTruckingOrganizationAdmin &&
            organization.registrationStatus === organizationRegistrationStatusesEnum.Completed,
        [roles?.isTruckingOrganizationAdmin, organization.registrationStatus],
    );

    const openRelayConnectionModal = useCallback(() => {
        setIsRelayModalOpened(true);
        props.modal(getRelayConnectionModalParams(props.getOrg, { initialStep: relayConnectionInitialStep }));
    }, [props.getOrg, props.modal, relayConnectionInitialStep]);

    const updateOnboardingOptions = useCallback((nextOnboardingOptions) => {
        onboardingOptionsRef.current = { ...initialOnboarding, ...nextOnboardingOptions };
    }, []);

    const requestApplyOnboarding = useCallback(
        async (payload) => {
            if (isOnboardingSuppressed) {
                return;
            }

            const nextOnboarding = {
                ...onboardingOptionsRef.current,
                ...payload,
            };

            updateOnboardingOptions(nextOnboarding);

            await applyOnboarding(nextOnboarding);
        },
        [isOnboardingSuppressed, updateOnboardingOptions],
    );

    const requestGetOnboarding = useCallback(async () => {
        setIsLoading(true);

        if (isOnboardingSuppressed) {
            setIsLoading(false);
            return;
        }

        try {
            const response = await getOnboarding();

            if (response) {
                updateOnboardingOptions(response);
            }
        } catch (error) {
            console.error("Failed to get onboarding data", error);
        } finally {
            setIsLoading(false);
        }
    }, [isOnboardingSuppressed, updateOnboardingOptions]);

    const requestApplyOnboardingIncCount = async () => {
        const payload = { onboardingCount: Number(onboardingOptionsRef.current.onboardingCount || 0) + 1 };

        await requestApplyOnboarding(payload);
    };

    const requestApplyOnboardingFinished = useCallback(async () => {
        const payload = { isOnboardingFinished: true };

        await requestApplyOnboarding(payload);
        sendEventLog({
            name: productMetricsEventNames.OnboardingCompleted,
            payload,
        });
    }, [requestApplyOnboarding]);

    const showModal = () => {
        if (isOnboardingSuppressed) {
            return;
        }

        const isDemoPeriodExpired = organization.demoPeriod?.status === organizationDemoPeriodStatusesEnum.Expired;
        const hasActiveBidKeyFeaturesReview = isActiveBidKeyFeaturesReview(organization.bidKeyFeaturesReview);
        const onboardingOptions = onboardingOptionsRef.current;

        if (isCompletedTruckingOrganizationAdmin() && showConnectionModal) {
            return;
        }

        if (
            hasActiveBidKeyFeaturesReview ||
            isDemoPeriodExpired ||
            onboardingOptions.isOnboardingFinished ||
            onboardingOptions.onboardingCount >= SHOW_LIMIT
        ) {
            return;
        }

        requestApplyOnboardingIncCount();
        sendEventLog({
            name: productMetricsEventNames.OnboardingStarted,
            payload: {
                onboardingCount: Number(onboardingOptions.onboardingCount || 0) + 1,
            },
        });

        props.modal({
            name: "registrationSuccess",
            withoutEscClose: false,
            mod: "registration-success",
            windowMod: "wide",
        });
    };

    useEffect(() => {
        requestGetOnboarding();
    }, [requestGetOnboarding]);

    useEffect(() => {
        if (isOnboardingSuppressed) {
            return;
        }

        window.addEventListener("onboardingFinished", requestApplyOnboardingFinished);

        return () => {
            window.removeEventListener("onboardingFinished", requestApplyOnboardingFinished);
        };
    }, [isOnboardingSuppressed, requestApplyOnboardingFinished]);

    useEffect(() => {
        if (!showConnectionModal) {
            setIsRelayModalOpened(false);
            return;
        }

        if (isCompletedTruckingOrganizationAdmin() && !isRelayModalOpened) {
            openRelayConnectionModal();
        }
    }, [showConnectionModal, isCompletedTruckingOrganizationAdmin, isRelayModalOpened, openRelayConnectionModal]);

    useEffect(() => {
        const token = localStorage.getItem("token");

        if (!token || !isCompletedTruckingOrganizationAdmin()) {
            return;
        }

        const connection = new SignalRConnection({
            url: "/signalr/organizations",
            actions: [
                {
                    name: "ShowRelayConnectionNeedAlert",
                    action: () => props.getOrg?.(),
                },
                {
                    name: "ShowRelayConnectionModal",
                    action: () => props.getOrg?.(),
                },
            ],
            joinActionName: "Join",
            leaveActionName: "Leave",
        });

        return () => {
            connection.disconnect();
        };
    }, [isCompletedTruckingOrganizationAdmin, props.getOrg]);

    useEffect(() => {
        if (isOnboardingSuppressed || isLoading) {
            return;
        }

        const isSelfRegistered = !!organization?.registrationStatus;

        if (isSelfRegistered && organization.registrationStatus === organizationRegistrationStatusesEnum.Completed) {
            showModal();
        }
    }, [isLoading, isOnboardingSuppressed, organization.registrationStatus]);

    return null;
};

const MonitoringWrapper = (props) => {
    if (!("registrationStatus" in props.organization)) {
        return null;
    }

    return <Monitoring {...props} />;
};

export default MonitoringWrapper;
